import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import * as styles from './Tooltip.css'

// =============================================================================
// Tooltip Provider
// =============================================================================

type TooltipProviderProps = TooltipPrimitive.TooltipProviderProps

const TooltipProvider = TooltipPrimitive.Provider

// =============================================================================
// Tooltip Root
// =============================================================================

type TooltipRootProps = TooltipPrimitive.TooltipProps

const TooltipRoot = TooltipPrimitive.Root

// =============================================================================
// Tooltip Trigger
// =============================================================================

type TooltipTriggerProps = TooltipPrimitive.TooltipTriggerProps

const TooltipTrigger = TooltipPrimitive.Trigger

// =============================================================================
// Tooltip Portal
// =============================================================================

type TooltipPortalProps = TooltipPrimitive.TooltipPortalProps

const TooltipPortal = TooltipPrimitive.Portal

// =============================================================================
// Tooltip Content
// =============================================================================

type TooltipContentProps = TooltipPrimitive.TooltipContentProps & {
  showArrow?: boolean
}

const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 4, showArrow = true, children, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={`${styles.content} ${className ?? ''}`}
    {...props}
  >
    {children}
    {showArrow && <TooltipPrimitive.Arrow className={styles.arrow} />}
  </TooltipPrimitive.Content>
))

TooltipContent.displayName = 'Tooltip.Content'

// =============================================================================
// Simple Tooltip (convenience component)
// =============================================================================

type SimpleTooltipProps = {
  content: React.ReactNode
  children: React.ReactNode
  side?: TooltipContentProps['side']
  align?: TooltipContentProps['align']
  delayDuration?: number
  showArrow?: boolean
}

const SimpleTooltip: React.FC<SimpleTooltipProps> = ({
  content,
  children,
  side = 'top',
  align = 'center',
  delayDuration,
  showArrow = true,
}) => (
  <TooltipRoot delayDuration={delayDuration}>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipPortal>
      <TooltipContent side={side} align={align} showArrow={showArrow}>
        {content}
      </TooltipContent>
    </TooltipPortal>
  </TooltipRoot>
)

// =============================================================================
// Exports
// =============================================================================

export const Tooltip = Object.assign(SimpleTooltip, {
  Provider: TooltipProvider,
  Root: TooltipRoot,
  Trigger: TooltipTrigger,
  Portal: TooltipPortal,
  Content: TooltipContent,
})

export type {
  TooltipProviderProps,
  TooltipRootProps,
  TooltipTriggerProps,
  TooltipPortalProps,
  TooltipContentProps,
  SimpleTooltipProps,
}
